import React, { useState, useEffect } from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/css";
import ModalLayout from "../../../components/structure/ModalLayout";
import ModalHeader from "../../../components/general/ModalHeader";
import FooterBar from "../../../components/structure/FooterBar";
import Button from "../../../components/general/Button";
import CustomScrollbars from "../../../components/structure/CustomScrollbars";
import useWindowSize from "../../../hooks/UseWindowSize";
import WebhookModel from "../../../models/webhooks/api/WebhookModel";
import { color_shades_dark, color_shades_darker, color_text_default, color_text_light, silver_four } from "../../../constants/colors";

interface WebhookOverridesModalProps {
    isVisible: boolean;
    onClose: () => void;
    webhooks: WebhookModel[];
    applicationId: string;
    currentOverrides: { [webhookId: string]: string };
    onSave: (overrides: { [webhookId: string]: string }) => void;
}

const WebhookOverridesModal: React.FC<WebhookOverridesModalProps> = (props) => {
    const [overrides, setOverrides] = useState<{ [webhookId: string]: string }>({});
    const windowSize = useWindowSize();
    const MODAL_SCROLLBAR_SUBTRACTION = 185;

    useEffect(() => {
        if (props.isVisible) {
            // Pre-populate every webhook: use the saved override if one exists, otherwise use the webhook's default URL
            const initial: { [webhookId: string]: string } = {};
            props.webhooks?.forEach(wh => {
                initial[wh.id] = props.currentOverrides[wh.id] ?? wh.url ?? "";
            });
            setOverrides(initial);
        }
    }, [props.isVisible, props.webhooks]);

    const handleOverrideChange = (webhookId: string, url: string) => {
        setOverrides(prev => ({
            ...prev,
            [webhookId]: url
        }));
    };

    const handleClearAll = () => {
        // Reset all inputs back to their default webhook URLs
        const reset: { [webhookId: string]: string } = {};
        props.webhooks?.forEach(wh => {
            reset[wh.id] = wh.url ?? "";
        });
        setOverrides(reset);
    };

    const handleSave = () => {
        // Only persist entries where the URL differs from the webhook's default
        const effectiveOverrides: { [webhookId: string]: string } = {};
        Object.entries(overrides).forEach(([webhookId, url]) => {
            const webhook = props.webhooks?.find(wh => wh.id === webhookId);
            const trimmedUrl = url?.trim() ?? "";
            if (trimmedUrl.length > 0 && trimmedUrl !== webhook?.url) {
                effectiveOverrides[webhookId] = trimmedUrl;
            }
        });
        props.onSave(effectiveOverrides);
        props.onClose();
    };

    const activeOverrideCount = props.webhooks?.filter(wh => {
        const val = overrides[wh.id]?.trim() ?? "";
        return val.length > 0 && val !== wh.url;
    }).length ?? 0;

    return (
        <ModalLayout isVisible={props.isVisible} onClose={props.onClose}>
            <ModalHeader
                title="Webhook URL Overrides"
                onClose={props.onClose}
                rightText={activeOverrideCount > 0 ? `${activeOverrideCount} overridden` : undefined}
            />
            <CustomScrollbars
                autoHide
                autoHeight
                autoHeightMax={(windowSize.windowSize.innerHeight * .9) - MODAL_SCROLLBAR_SUBTRACTION}
            >
                <Body>
                    <Description>
                        Override webhook URLs for simulator and test case requests.
                        These overrides are saved locally and will not affect live application behavior.
                    </Description>
                    {props.webhooks?.length > 0 ? (
                        <WebhookList>
                            {props.webhooks.map(webhook => {
                                const isOverridden = (overrides[webhook.id]?.trim() ?? "") !== "" && overrides[webhook.id]?.trim() !== webhook.url;
                                return (
                                    <WebhookRow key={webhook.id}>
                                        <WebhookInfo>
                                            <WebhookTitle>{webhook.title}{isOverridden && <OverrideLabel>Overridden</OverrideLabel>}</WebhookTitle>
                                        </WebhookInfo>
                                        <OverrideInput
                                            type="text"
                                            placeholder="Enter URL"
                                            value={overrides[webhook.id] ?? ""}
                                            onChange={e => handleOverrideChange(webhook.id, e.target.value)}
                                        />
                                    </WebhookRow>
                                );
                            })}
                        </WebhookList>
                    ) : (
                        <EmptyState>
                            No webhooks are configured for this application.
                        </EmptyState>
                    )}
                </Body>
            </CustomScrollbars>
            <FooterBar>
                <Button
                    type="button"
                    themes={["secondary", "start"]}
                    text="Clear All"
                    onClick={handleClearAll}
                    disabled={activeOverrideCount === 0}
                />
                <Button
                    type="button"
                    themes={["primary", "end"]}
                    text="Save"
                    onClick={handleSave}
                />
            </FooterBar>
        </ModalLayout>
    );
};

export default WebhookOverridesModal;

const Body = styled.div`
    padding: 24px 32px;
`;

const Description = styled.p`
    font-family: Muli;
    font-size: 14px;
    line-height: 20px;
    color: ${color_text_light};
    margin: 0 0 24px 0;
`;

const WebhookList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const WebhookRow = styled.div`
    border: 1px solid ${silver_four};
    border-radius: 8px;
    padding: 16px;
`;

const WebhookInfo = styled.div`
    margin-bottom: 8px;
`;

const WebhookTitle = styled.div`
    font-family: Muli;
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
    color: ${color_text_default};
    display: flex;
    align-items: center;
    gap: 8px;
`;

const OverrideLabel = styled.span`
    font-size: 11px;
    font-weight: 600;
    line-height: 16px;
    color: #e67e22;
    background: #fef5ec;
    border-radius: 4px;
    padding: 1px 6px;
`;

const OverrideInput = styled.input`
    width: 100%;
    padding: 8px 12px;
    border: 1px solid ${color_shades_dark};
    border-radius: 4px;
    font-family: Muli;
    font-size: 14px;
    line-height: 20px;
    box-sizing: border-box;
    &:focus {
        outline: none;
        border-color: #3498db;
    }
    &::placeholder {
        color: ${color_shades_darker};
        font-style: italic;
    }
`;

const EmptyState = styled.div`
    font-family: Muli;
    font-size: 14px;
    line-height: 20px;
    color: ${color_text_light};
    text-align: center;
    padding: 48px 0;
`;
